<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-bold text-gray-800">
        {{ $t("activity_logs") }}
      </h1>
      <div class="flex gap-2">
        <BaseButton type="secondary" @click="resetFilters">
          {{ $t("reset_filters") }}
        </BaseButton>
        <BaseButton type="primary" @click="fetchActivityLogs">
          {{ $t("refresh_data") }}
        </BaseButton>
      </div>
    </div>

    <div class="bg-white p-4 rounded-lg shadow-sm space-y-4">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t("moderator") }}
          </label>
          <select
            v-model="filters.user_id"
            @change="fetchActivityLogs"
            class="w-full border rounded-md p-2"
          >
            <option :value="null">
              {{ $t("all") }}
            </option>
            <option
              v-for="moderator in moderators"
              :key="moderator.id"
              :value="moderator.id"
            >
              {{ moderator.full_name_en || moderator.username || "Unknown" }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t("action") }}
          </label>
          <select
            v-model="filters.action"
            @change="fetchActivityLogs"
            class="w-full border rounded-md p-2"
          >
            <option value="">
              {{ $t("all") }}
            </option>
            <option
              v-for="action in actions"
              :key="action.value"
              :value="action.value"
            >
              {{ action.label }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t("period") }}
          </label>
          <select
            v-model="filters.period"
            @change="periodChanged"
            class="w-full border rounded-md p-2"
          >
            <option value="">
              {{ $t("all") }}
            </option>
            <option value="today">
              {{ $t("today") }}
            </option>
            <option value="yesterday">
              {{ $t("yesterday") }}
            </option>
            <option value="this_week">
              {{ $t("this_week") }}
            </option>
            <option value="last_week">
              {{ $t("last_week") }}
            </option>
            <option value="this_month">
              {{ $t("this_month") }}
            </option>
            <option value="last_month">
              {{ $t("last_month") }}
            </option>
            <option value="last_7_days">
              {{ $t("last_7_days") }}
            </option>
            <option value="last_30_days">
              {{ $t("last_30_days") }}
            </option>
            <option value="custom">
              {{ $t("custom") }}
            </option>
          </select>
        </div>
      </div>

      <div
        v-if="filters.period === 'custom'"
        class="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t("from") }}
          </label>
          <input
            type="datetime-local"
            v-model="filters.from"
            @change="fetchActivityLogs"
            class="w-full border rounded-md p-2"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t("to") }}
          </label>
          <input
            type="datetime-local"
            v-model="filters.to"
            @change="fetchActivityLogs"
            class="w-full border rounded-md p-2"
          />
        </div>
      </div>
    </div>

    <BaseDataTable :columns="columns" :data="activityLogs" :loading="loading">
      <template #created_at="{ row }">
        <div class="flex flex-col">
          <span class="font-bold text-gray-900">
            {{ formatDate(row.created_at) }}
          </span>

          <span class="text-xs font-semibold text-gray-500">
            {{ formatTime(row.created_at) }}
          </span>
        </div>
      </template>
      <template #user="{ row }">
        {{ row.user?.full_name_en || row.user?.username || "-" }}
      </template>
      <template #action="{ row }">
        <span
          class="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"
        >
          {{ formatAction(row.action) }}
        </span>
      </template>
      <template #subject="{ row }">
        {{ formatSubject(row.subject_type) }}
      </template>
      <!-- <template #context="{ row }">
        <span class="truncate block max-w-xs">
          {{ contextPreview(row.context) }}
        </span>
      </template> -->
      <template #actions="{ row }">
        <button
          @click="openDetails(row)"
          class="text-blue-600 hover:text-blue-800 font-medium"
        >
          {{ $t("view") }}
        </button>
      </template>
    </BaseDataTable>

    <BasePagination
      v-if="pagination"
      :pagination="pagination"
      @change-page="handlePageChange"
    />
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import BasePagination from "@/components/BasePagination.vue";
import Swal from "sweetalert2";
import activityLogService from "@/services/activityLog.service";

export default {
  name: "ActivityLogsList",
  components: { BaseButton, BaseDataTable, BasePagination },
  data() {
    return {
      loading: false,
      activityLogs: [],
      pagination: null,
      moderators: [],
      actions: [
        { value: "create_car", label: "Create Car" },
        { value: "update_car", label: "Update Car" },
        { value: "delete_car", label: "Delete Car" },
        { value: "approve_car", label: "Approve Car" },
        { value: "reject_car", label: "Reject Car" },
        { value: "create_user", label: "Create User" },
        { value: "update_user", label: "Update User" },
        { value: "delete_user", label: "Delete User" },
        { value: "login", label: "Login" },
        { value: "logout", label: "Logout" },
      ],
      columns: [],
      filters: {
        user_id: null,
        action: "",
        period: "",
        from: "",
        to: "",
        page: 1,
        per_page: 10,
      },
    };
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler() {
        this.updateColumns();
      },
    },
  },
  async mounted() {
    await Promise.all([this.fetchModerators(), this.fetchActivityLogs()]);
  },
  methods: {
    updateColumns() {
      this.columns = [
        { label: this.$t("date"), field: "created_at" },
        { label: this.$t("moderator"), field: "user" },
        { label: this.$t("action"), field: "action" },
        { label: this.$t("subject"), field: "subject" },
        // { label: this.$t("context"), field: "context" },
      ];
    },
    async fetchModerators() {
      try {
        const res = await activityLogService.getModerators();
        this.moderators = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchActivityLogs() {
      this.loading = true;
      try {
        const params = { ...this.filters };
        Object.keys(params).forEach((key) => {
          if (
            params[key] === "" ||
            params[key] === null ||
            params[key] === undefined
          ) {
            delete params[key];
          }
        });
        const res = await activityLogService.getActivityLogs(params);
        if (res.data.pagination) {
          this.activityLogs = res.data.data;
          this.pagination = res.data.pagination;
        } else {
          this.activityLogs = res.data.data;
          this.pagination = null;
        }
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          this.$t("failed_fetch_activity_logs"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.filters.page = page;
      this.fetchActivityLogs();
    },
    periodChanged() {
      if (this.filters.period !== "custom") {
        this.filters.from = "";
        this.filters.to = "";
      }
      this.fetchActivityLogs();
    },
    resetFilters() {
      this.filters = {
        user_id: null,
        action: "",
        period: "",
        from: "",
        to: "",
        page: 1,
        per_page: 10,
      };
      this.fetchActivityLogs();
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatAction(action) {
      return action
        .replaceAll("_", " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },
    formatSubject(subject) {
      if (!subject) return "-";
      return subject.split("\\").pop();
    },
    contextPreview(context) {
      if (!context) return "-";
      if (typeof context === "object") return JSON.stringify(context);
      return context;
    },
    openDetails(activity) {
      Swal.fire({
        title: this.$t("activity_details") || "Activity Details",
        html: `
          <div class="text-left space-y-2">
            <p><strong>ID:</strong> ${activity.id}</p>
            <p><strong>${this.$t("date") || "Date"}:</strong> ${this.formatDate(
          activity.created_at
        )}</p>
            <p><strong>${this.$t("time") || "Time"}:</strong> ${this.formatTime(
          activity.created_at
        )}</p>
            <p><strong>${this.$t("moderator") || "User"}:</strong> ${
          activity.user?.full_name_en || activity.user?.username || "-"
        }</p>
            <p><strong>${
              this.$t("action") || "Action"
            }:</strong> ${this.formatAction(activity.action)}</p>
            <p><strong>${
              this.$t("subject") || "Subject"
            }:</strong> ${this.formatSubject(activity.subject_type)} (ID: ${
          activity.subject_id || "-"
        })</p>
            <p><strong>${
              this.$t("context") || "Context"
            }:</strong> ${this.contextPreview(activity.context)}</p>
            ${
              activity.old_values
                ? `<p><strong>Old Values:</strong> <pre class="text-sm">${JSON.stringify(
                    activity.old_values,
                    null,
                    2
                  )}</pre></p>`
                : ""
            }
            ${
              activity.new_values
                ? `<p><strong>New Values:</strong> <pre class="text-sm">${JSON.stringify(
                    activity.new_values,
                    null,
                    2
                  )}</pre></p>`
                : ""
            }
            <p><strong>IP Address:</strong> ${activity.ip_address || "-"}</p>
            <p><strong>User Agent:</strong> ${activity.user_agent || "-"}</p>
          </div>
        `,
        confirmButtonText: this.$t("close") || "Close",
        confirmButtonColor: "#3085d6",
      });
    },
  },
};
</script>
